'use client';

import './infinite-scroll.css';

import AddPhotoAlternateOutlinedIcon from '@mui/icons-material/AddPhotoAlternateOutlined';
import { CommunityGameWithContent } from '@wt/app/community/types';
import { getPuzzle } from '@wt/app/game/_utils/getPuzzle';
import { useUserProvider } from '@wt/game/providers/user/UserProvider';
import { GameTile } from '@wt/shared/components/games/GameTile';
import { Spinner } from '@wt/shared/components/spinner/spinner';
import { getMyGames } from '@wt/utilities/database';
import { isEmpty } from 'lodash';
import Link from 'next/link';
import { useCallback, useEffect, useRef, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';

import { GAMES_PER_LOAD } from './CommunityContent';

export const MyGamesTabContent = ({
  handleClickGame,
}: {
  handleClickGame: (slug: string) => void;
}) => {
  const [totalCount, setTotalCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [games, setGames] = useState<CommunityGameWithContent[]>([]);
  const gamesContainer = useRef<HTMLDivElement>(null);

  const loadNext = useCallback(async () => {
    const { games: loadedGames, totalCount: loadedTotalCount } =
      await getMyGames({
        offset: games.length,
        limit: GAMES_PER_LOAD,
      });
    const promises: Promise<CommunityGameWithContent>[] = [];
    loadedGames.forEach((g) => {
      promises.push(
        getPuzzle(g.slug, 'community').then((rounds) => {
          const gameWithContent: CommunityGameWithContent = {
            ...g,
            rounds,
          };
          return gameWithContent;
        })
      );
    });
    const g = await Promise.all(promises);
    setGames([...games, ...g]);
    setTotalCount(loadedTotalCount);
  }, [games, setTotalCount]);

  useEffect(() => {
    (async () => {
      await loadNext();
      setLoading(false);
    })();
  }, []);

  const hasMore = totalCount > games.length;

  return (
    <div className="mb-5 mt-3 flex w-full flex-col items-center justify-center px-4 md:my-5">
      <div className="flex w-full flex-col items-center gap-3 md:max-w-screen-xl">
        <div ref={gamesContainer} className="w-full items-center">
          {loading && (
            <div className="flex w-full justify-center">
              <Spinner />
            </div>
          )}
          {!loading && (
            <InfiniteScroll
              dataLength={games.length}
              next={loadNext}
              hasMore={hasMore}
              loader={
                <div className="mt-4 flex w-full justify-center">
                  <Spinner />
                </div>
              }
            >
              <GamesContent games={games} handleClickGame={handleClickGame} />
            </InfiniteScroll>
          )}
        </div>
      </div>
    </div>
  );
};

const GamesContent = ({
  games,
  handleClickGame,
}: {
  games: CommunityGameWithContent[];
  handleClickGame: (slug: string) => void;
}) => {
  return (
    <div className="flex w-full items-center justify-center">
      {isEmpty(games) && <EmptyGamesContent />}
      {!isEmpty(games) && (
        <GamesList games={games} handleClickGame={handleClickGame} />
      )}
    </div>
  );
};

const GamesList = ({
  games,
  handleClickGame,
}: {
  games: CommunityGameWithContent[];
  handleClickGame: (slug: string) => void;
}) => {
  return (
    <ul className="grid w-full grid-cols-1 gap-3 md:grid-cols-3 lg:grid-cols-4">
      {games.map((g, i) => (
        <GameTile
          key={i}
          game={g}
          handleClick={handleClickGame}
          features={{
            name: true,
            edit: !g.banned,
            share: !g.banned,
            play: true,
            publicIcon: !g.banned,
          }}
        />
      ))}
    </ul>
  );
};

const EmptyGamesContent = () => {
  return (
    <div className="flex size-full flex-col items-center justify-center gap-6 text-center md:max-w-[364px]">
      <div className="text-6xl text-highlight">
        <AddPhotoAlternateOutlinedIcon fontSize="inherit" />
      </div>
      <span className="text-center text-sm md:text-lg">
        It looks like you haven't created any games yet. Get started by clicking
        the button below to bring your ideas to life.
      </span>
      <CreateGameTextButton />
      <div className="flex w-full flex-col gap-3 text-xs">
        <span>
          Before you begin, please take a moment to read our{' '}
          <Link
            href={'/community/guidelines'}
            className="text-info-content underline"
          >
            Community Guidelines
          </Link>
          .
        </span>
        <span>
          Remember, it's your responsibility to help keep our community safe,
          respectful, and fun for everyone. Thank you for joining us and happy
          creating!
        </span>
      </div>
    </div>
  );
};

const CreateGameTextButton = () => {
  return (
    <Link
      href={'/account/games/new'}
      className="btn btn-primary h-[40px] min-h-[40px] w-fit shrink whitespace-nowrap rounded-lg text-base font-medium uppercase md:text-xl"
    >
      <span className="pointer-events-none whitespace-nowrap">
        <AddPhotoAlternateOutlinedIcon className="pr-1" /> Create game
      </span>
    </Link>
  );
};

export const CreateGameButton = () => {
  const { loggedIn } = useUserProvider();

  return (
    <>
      <Link
        href="/account/games/new"
        className="btn btn-primary absolute -top-2 right-0 flex h-[40px] min-h-[40px] w-fit shrink whitespace-nowrap rounded-lg px-1 text-3xl font-medium md:hidden"
      >
        <AddPhotoAlternateOutlinedIcon
          className="pointer-events-none"
          fontSize="inherit"
        />
      </Link>
      <div className="hidden md:flex">
        <Link
          href="/account/games/new"
          className="btn btn-primary absolute -top-2 right-0 h-[40px] min-h-[40px] w-fit shrink whitespace-nowrap rounded-lg text-xl font-medium"
        >
          <span className="pointer-events-none whitespace-nowrap">
            <AddPhotoAlternateOutlinedIcon className="pr-1" /> Create game
          </span>
        </Link>
      </div>
    </>
  );
};
